import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);
const OFFLINE_USER_KEY = "fff_offline_admin";

export const AuthProvider = ({ children }) => {
  // null = checking, false = logged out, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // New shape: { user: {...} | null }. Older deployments may still return
      // the user dict directly — handle both for forward/backward compat.
      const u = data && Object.prototype.hasOwnProperty.call(data, "user")
        ? data.user
        : data;
      setUser(u || false);
      return u || null;
    } catch (e) {
      // No signal: keep a previously verified admin signed in so the
      // offline Event Capture tool still opens (tokens stay as they were).
      const cached = !e?.response && localStorage.getItem(OFFLINE_USER_KEY);
      if (cached) {
        const u = JSON.parse(cached);
        setUser(u);
        return u;
      }
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") {
      localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify({ id: user.id, role: user.role, name: user.name, email: user.email }));
    } else if (user === false && navigator.onLine) {
      localStorage.removeItem(OFFLINE_USER_KEY);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("fff_access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("fff_refresh_token", data.refresh_token);
      // Strip token fields before saving user
      const { access_token, refresh_token, ...rest } = data;
      setUser(rest);
      return { ok: true, user: rest };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // Newer backend returns { user, pending_approval }. Pending agency
      // accounts are NOT logged in immediately — surface that state up so
      // the UI can show an "Awaiting approval" message instead of routing
      // them to the portal.
      if (data && typeof data === "object" && "pending_approval" in data) {
        if (data.pending_approval) {
          setUser(false); // remain logged out
          return { ok: true, pending: true, user: data.user };
        }
        setUser(data.user);
      } else {
        setUser(data);
      }
      // Try to log in to capture access token in localStorage as fallback.
      // Skip for pending agency accounts (login will be blocked server-side).
      try {
        const login = await api.post("/auth/login", { email: payload.email, password: payload.password });
        if (login.data?.access_token) localStorage.setItem("fff_access_token", login.data.access_token);
        if (login.data?.refresh_token) localStorage.setItem("fff_refresh_token", login.data.refresh_token);
      } catch (err) {
        if (typeof console !== "undefined") console.debug("auth: fallback login skipped", err?.response?.status);
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      if (typeof console !== "undefined") console.debug("auth: logout request failed", err?.response?.status);
    }
    localStorage.removeItem("fff_access_token");
    localStorage.removeItem("fff_refresh_token");
    setUser(false);
  };

  // Stable context value so consumers don't re-render on every parent
  // re-render. login/register/logout are recreated each render but they
  // never depend on the latest closure (no captured state), so excluding
  // them from deps is safe and matches Auth0/Firebase's idiomatic pattern.
  const value = useMemo(
    () => ({ user, login, register, logout, refresh }),
    [user, refresh] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
